import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { createApiClient } from "../api/client";
import { CreateMemberRequest, DataSourceSetting, EventSourceProvider, Family, Member, MemberRole } from "../types";
import Button from "./ui/Button";

const SOURCE_LABELS: Record<EventSourceProvider, string> = {
  GooglePlaces: "Google Places (restaurants & venues)",
  Ticketmaster: "Ticketmaster (events & shows)",
  GoogleSearch: "Google Search (web)",
  MovieGlu: "MovieGlu (movie showtimes)",
  StaticSeed: "Built-in sample options",
};

const ROLES: MemberRole[] = ["Adult", "Teen", "Child"];

const INPUT =
  "rounded-xl border border-primary-200 bg-white/70 px-3.5 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200";

const LABEL = "text-xs font-semibold uppercase tracking-wide text-ink/45";

/** Manage one family: start a plan, view/add members, toggle data sources. */
const FamilyManagePanel: React.FC<{
  family: Family;
  onChanged: () => void;
  onStart: () => void;
}> = ({ family, onChanged, onStart }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [name, setName] = useState("");
  const [role, setRole] = useState<MemberRole>("Adult");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [sources, setSources] = useState<DataSourceSetting[]>([]);

  const loadSources = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const { data } = await createApiClient(token).get<DataSourceSetting[]>(
        `/api/families/${family.id}/data-sources`
      );
      setSources(data);
    } catch {
      /* non-fatal: data-source config is optional */
    }
  }, [getAccessTokenSilently, family.id]);

  useEffect(() => {
    loadSources();
  }, [loadSources]);

  const toggleSource = async (s: DataSourceSetting) => {
    const token = await getAccessTokenSilently();
    const { data } = await createApiClient(token).put<DataSourceSetting[]>(
      `/api/families/${family.id}/data-sources`,
      { provider: s.provider, enabled: !s.enabled, radiusMeters: s.radiusMeters ?? null }
    );
    setSources(data);
  };

  const removeMember = async (memberId: string, name: string) => {
    if (!window.confirm(`Remove ${name} from this family?`)) return;
    const token = await getAccessTokenSilently();
    await createApiClient(token).delete(`/api/families/${family.id}/members/${memberId}`);
    onChanged();
  };

  const [inviting, setInviting] = useState<string | null>(null);

  // Mint a "join the family" link and share it: text it (if they have a number)
  // and copy it to the clipboard. The backend also auto-texts when SMS is live.
  const inviteToFamily = async (m: Member) => {
    setInviting(m.id);
    try {
      const token = await getAccessTokenSilently();
      const { data } = await createApiClient(token).post<{ inviteUrl: string }>(
        `/api/families/${family.id}/members/${m.id}/family-invite`,
        {}
      );
      try {
        await navigator.clipboard.writeText(data.inviteUrl);
      } catch {
        /* clipboard may be blocked; the sms/link still works */
      }
      if (m.mobileNumber) {
        const body = `Join the "${family.name}" family on Kinoes: ${data.inviteUrl}`;
        window.location.href = `sms:${m.mobileNumber}?&body=${encodeURIComponent(body)}`;
      } else {
        window.prompt("Copy this join link and send it to them:", data.inviteUrl);
      }
      onChanged();
    } finally {
      setInviting(null);
    }
  };

  const hasContact = mobile.trim() !== "" || email.trim() !== "";

  const addMember = async () => {
    if (!name.trim()) return;
    if (hasContact && !consent) return; // consent required before storing a contact
    const token = await getAccessTokenSilently();
    const body: CreateMemberRequest = {
      displayName: name.trim(),
      role,
      mobileNumber: mobile.trim() || null,
      email: email.trim() || null,
    };
    await createApiClient(token).post(`/api/families/${family.id}/members`, body);
    setName("");
    setMobile("");
    setEmail("");
    setRole("Adult");
    setConsent(false);
    onChanged();
  };

  return (
    <div>
      <Button fullWidth size="lg" onClick={onStart}>
        Start tonight's plan
      </Button>

      <p className={`mt-6 ${LABEL}`}>Members</p>
      <ul className="mt-2 divide-y divide-primary-100">
        {family.members.length === 0 && <li className="py-2 text-sm text-ink/40">No members yet.</li>}
        {family.members.map((m) => (
          <li key={m.id} className="flex items-center justify-between gap-2 py-2 text-sm">
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate text-ink">{m.displayName}</span>
              {m.status === "Pending" && (
                <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                  Invite pending
                </span>
              )}
              {m.status === "Declined" && (
                <span className="shrink-0 rounded-full bg-ink/10 px-2 py-0.5 text-[11px] font-medium text-ink/55">
                  Declined
                </span>
              )}
            </span>
            <span className="flex shrink-0 items-center gap-2">
              {m.status !== "Accepted" && (
                <button
                  onClick={() => inviteToFamily(m)}
                  disabled={inviting === m.id}
                  className="rounded-full border border-primary-300 px-2.5 py-0.5 text-xs font-medium text-primary-700 hover:border-primary-400 disabled:opacity-50"
                >
                  {inviting === m.id ? "…" : m.status === "Declined" ? "Re-invite" : "Invite"}
                </button>
              )}
              <span className="text-ink/55">
                {m.role}
                {m.mobileNumber ? ` · ${m.mobileNumber}` : ""}
              </span>
              <button
                onClick={() => removeMember(m.id, m.displayName)}
                aria-label={`Remove ${m.displayName}`}
                className="rounded-full px-2 py-0.5 text-base leading-none text-ink/35 hover:bg-red-50 hover:text-red-600"
              >
                ×
              </button>
            </span>
          </li>
        ))}
      </ul>

      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-4">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name"
          className={`${INPUT} sm:col-span-2`}
        />
        <select value={role} onChange={(e) => setRole(e.target.value as MemberRole)} className={INPUT}>
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <input
          value={mobile}
          onChange={(e) => setMobile(e.target.value)}
          placeholder="+1555…"
          className={INPUT}
        />
      </div>
      <input
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        type="email"
        placeholder="Email (optional — for plan & reminder notices)"
        className={`${INPUT} mt-2 w-full`}
      />
      <label className="mt-3 flex items-start gap-2.5 text-xs text-ink/60">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-primary-300 text-primary-500 focus:ring-primary-300"
        />
        <span>
          I confirm this person has agreed to receive Kinoes messages (invitations, plan &amp; reminder
          notices). They can reply STOP to opt out; Msg &amp; data rates may apply. See our{" "}
          <Link to="/sms-policy" className="underline hover:text-primary-600">
            SMS Policy
          </Link>{" "}
          and{" "}
          <Link to="/privacy" className="underline hover:text-primary-600">
            Privacy Policy
          </Link>
          .
        </span>
      </label>
      <Button
        variant="secondary"
        className="mt-2"
        onClick={addMember}
        disabled={hasContact && !consent}
        title={hasContact && !consent ? "Confirm consent to add a contact" : undefined}
      >
        Add member
      </Button>

      {sources.length > 0 && (
        <div className="mt-6 border-t border-primary-100 pt-4">
          <p className={LABEL}>Data sources</p>
          <div className="mt-2 space-y-1.5">
            {sources.map((s) => (
              <label key={s.provider} className="flex items-center gap-2.5 text-sm text-ink/80">
                <input
                  type="checkbox"
                  checked={s.enabled}
                  onChange={() => toggleSource(s)}
                  className="h-4 w-4 rounded border-primary-300 text-primary-500 focus:ring-primary-300"
                />
                {SOURCE_LABELS[s.provider]}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyManagePanel;
